// src/adminApi.ts
// Thin fetch client for /api/mgmt/* — the admin author list, account status
// transitions, and per-account settings. Route lives under `mgmt`, not
// `admin` — Azure Functions reserves the `admin` route segment.

import { apiFetch } from './api';
import type { AuthorStatus } from './auth';
import type { AdminAuthorAction } from '@ring/shared/author-trust';

export interface AdminAuthor {
  userId:           string;
  email:            string;
  isAdmin:          boolean;
  createdAt:        string;
  lastLogin:        string | null;
  authorStatus:     AuthorStatus;
  statusNote:       string | null;
  statusUpdatedAt:  string | null;
  aiEnabled:        boolean;
  storageQuotaMb:   number;
  rendersPerDay:    number;
  aiImagesPerDay:   number;
}

export async function listAdminAuthors(): Promise<AdminAuthor[]> {
  const data = await apiFetch<{ authors: AdminAuthor[] }>('/api/mgmt/authors', { method: 'GET' });
  return data.authors;
}

/** `statusNote` is required by the server for reject/suspend (see AUTHOR_ACTIONS' requiresNote). */
export function runAuthorAction(userId: string, action: AdminAuthorAction, statusNote?: string): Promise<AdminAuthor> {
  return apiFetch<AdminAuthor>(`/api/mgmt/authors/${userId}/${action}`, {
    method: 'POST',
    body: statusNote ? JSON.stringify({ statusNote }) : undefined,
  });
}

export function updateAuthorSettings(
  userId: string, settings: { aiEnabled?: boolean; storageQuotaMb?: number; rendersPerDay?: number; aiImagesPerDay?: number },
): Promise<AdminAuthor> {
  return apiFetch<AdminAuthor>(`/api/mgmt/authors/${userId}/settings`, {
    method: 'POST',
    body: JSON.stringify(settings),
  });
}
